import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, TemplateRef, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { featureFlag, FeatureFlagPipe } from '@sb/shared/feature';
import { ButtonComponent, IconButtonComponent } from '@sb/ui/button';
import { DIALOG_DATA, DialogRef, SbDialogModule, SbDialogService } from '@sb/ui/dialog';
import { LoadingStateComponent } from '@sb/ui/feedback';
import { SbFormFieldComponent } from '@sb/ui/form-field';
import { IconComponent } from '@sb/ui/icon';
import { InputModule } from '@sb/ui/input';
import { LaddaDirective } from '@sb/ui/ladda';
import { RadioComponent } from '@sb/ui/radio';
import { RadioGroupComponent } from '@sb/ui/radio-group';
import { SelectComponent } from '@sb/ui/select';
import { filter, finalize, switchMap, take, tap } from 'rxjs';

import { fieldTypeOptions } from '../../field-type-options.helper';
import {
  CreateTemplateVariableData,
  TemplateUsingVariable,
  TemplateVariable,
} from '../../models/contract-template.model';
import { ContractTemplateService } from '../../services/contract-template.service';
import { variableNameFromLabel } from '../../variable-name.helper';

// A label of only special characters would generate an empty variable name,
// which the backend rejects and which used to poison the variables listing.
const labelGeneratesVariableNameValidator = (control: AbstractControl<string>): ValidationErrors | null =>
  !control.value || variableNameFromLabel(control.value) ? null : { labelWithoutVariableCharacters: true };

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'create-new-field-modal',
  templateUrl: './create-new-field-modal.component.html',
  styleUrl: './create-new-field-modal.component.scss',
  imports: [
    ButtonComponent,
    IconButtonComponent,
    SbDialogModule,
    ReactiveFormsModule,
    TranslatePipe,
    SelectComponent,
    InputModule,
    LaddaDirective,
    SbFormFieldComponent,
    FeatureFlagPipe,
    LoadingStateComponent,
    IconComponent,
    RadioComponent,
    RadioGroupComponent,
  ],
})
export class CreateNewFieldModalComponent implements OnInit {
  private dialogRef = inject(DialogRef);
  private formBuilder = inject(FormBuilder);
  private contractTemplateService = inject(ContractTemplateService);
  private destroyRef = inject(DestroyRef);
  private translateService = inject(TranslateService);
  private dialogService = inject(SbDialogService);
  private data = inject(DIALOG_DATA) as { field?: TemplateVariable };
  isEditMode = !!this.data?.field?.variable;

  loading = false;
  deleting = false;
  features = featureFlag;

  // The description is shown as a tooltip on the hiring form, so it has to stay short enough to fit
  DESCRIPTION_MAX_LENGTH = 120;

  fieldLabel = this.data?.field?.label ?? '';
  templatesUsingField: TemplateUsingVariable[] = [];
  // Not #-private: Angular disallows signal queries on ES-private members
  deleteConfirmContent = viewChild.required<TemplateRef<unknown>>('deleteConfirmContent');

  // Options handling
  options: string[] = [];
  optionNameControl = new FormControl('');

  form: FormGroup = this.formBuilder.group({
    label: ['', [Validators.required, labelGeneratesVariableNameValidator]],
    variable: [{ value: '', disabled: true }, Validators.required],
    description: ['', [Validators.maxLength(this.DESCRIPTION_MAX_LENGTH)]],
    internal: [{ value: true, disabled: this.isEditMode }],
    type: [{ value: 'text', disabled: this.isEditMode }, Validators.required],
    category: ['personal_information', Validators.required],
    options: [[]],
  });

  internalOptions = [
    { id: true, label: this.translateService.instant('Employer') },
    { id: false, label: this.translateService.instant('New hire') },
  ];

  typeOptions = fieldTypeOptions(this.translateService);

  categoryOptions = [
    { value: 'address', text: this.translateService.instant('Address') },
    { value: 'contact', text: this.translateService.instant('Contact Details') },
    { value: 'contract_information', text: this.translateService.instant('Contract Information') },
    { value: 'emergency_contact', text: this.translateService.instant('Emergency Contact') },
    { value: 'payroll', text: this.translateService.instant('Payroll') },
    { value: 'personal_information', text: this.translateService.instant('Personal Information') },
    { value: 'others', text: this.translateService.instant('Others') },
  ];

  ngOnInit() {
    if (this.isEditMode) {
      this.loading = true;
      void this.contractTemplateService
        .getSystemVariableByVariableName(this.data.field.variable)
        .pipe(
          finalize(() => {
            this.loading = false;
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe((field) => {
          this.form.patchValue({
            ...field,
            category: field.category?.key,
          });
          this.#revealStoredDescriptionError();

          if (field.options?.items) {
            this.options = field.options.items.map((item) =>
              typeof item === 'string' ? item : item.text || item.value,
            );
            this.form.patchValue({ options: this.options });
          }
        });
    }

    if (!this.isEditMode) {
      this.form.controls['label']?.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((labelValue: string) => {
          if (labelValue) {
            const variableValue = this.generateVariableFromLabel(labelValue);
            this.form.controls['variable']?.setValue(variableValue);
          } else {
            this.form.controls['variable']?.setValue('');
          }
        });
    }
  }

  // Descriptions saved before the limit existed can be too long, and the field only shows errors once it
  // is touched, so without this the user meets a disabled save button and no explanation of why
  #revealStoredDescriptionError(): void {
    const description = this.form.controls['description'];

    if (description?.invalid) {
      description.markAsTouched();
    }
  }

  private generateVariableFromLabel(label: string): string {
    return `{${variableNameFromLabel(label)}}`;
  }

  addOption(): void {
    const optionValue = this.optionNameControl.value?.trim();
    if (optionValue && !this.options.includes(optionValue)) {
      this.options.push(optionValue);
      this.form.patchValue({ options: this.options });
      this.optionNameControl.setValue('');
    }
  }

  removeOption(option: string): void {
    this.options = this.options.filter((opt) => opt !== option);
    this.form.patchValue({ options: this.options });
  }

  close() {
    this.dialogRef.close();
  }

  deleteField() {
    const field = this.data?.field;
    if (!field) {
      return;
    }

    void this.contractTemplateService
      .getTemplatesUsingVariable(field.variable)
      .pipe(
        switchMap((templates) => {
          this.templatesUsingField = templates;

          return this.#openDeleteConfirmation().closed;
        }),
        take(1),
        filter((result) => !!result?.confirmed),
        // Loading state only during the actual deletion, not while the confirmation is open
        tap(() => {
          this.deleting = true;
        }),
        switchMap(() => this.contractTemplateService.deleteTemplateVariable(field.variable)),
        finalize(() => {
          this.deleting = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        // Success toast comes from the backend's status_message via the meta interceptor
        this.dialogRef.close();
      });
  }

  #openDeleteConfirmation() {
    return this.dialogService.openConfirm({
      title: this.translateService.instant('Delete field'),
      type: 'warning',
      contentTemplate: this.deleteConfirmContent(),
      primary: {
        text: this.translateService.instant('Delete'),
      },
      secondary: {
        text: this.translateService.instant('Cancel'),
      },
    });
  }

  save() {
    this.form.markAllAsTouched();

    const hasNoOptionsForSave = this.form.controls['type']?.value === 'select' && this.options.length === 0;
    if (this.form.valid && !hasNoOptionsForSave) {
      const fieldData = this.form.getRawValue();
      const variableData: CreateTemplateVariableData = {
        label: fieldData.label,
        variable: fieldData.variable.replace(/[{}]/g, ''),
        description: fieldData.description,
        type: fieldData.type,
        category: fieldData.category,
        mandatory: true, // Set mandatory to true by default until we support custom questions
        internal: fieldData.internal,
        ...(fieldData.type === 'select' && { options: this.options }),
      };

      this.loading = true;

      if (this.isEditMode && this.data?.field) {
        void this.contractTemplateService
          .updateTemplateVariable(this.data.field.variable, variableData)
          .pipe(
            finalize(() => {
              this.loading = false;
            }),
          )
          .subscribe((response) => {
            this.dialogRef.close(response);
          });
      } else {
        void this.contractTemplateService
          .createTemplateVariable(variableData)
          .pipe(
            finalize(() => {
              this.loading = false;
            }),
          )
          .subscribe((response) => {
            this.dialogRef.close(response);
          });
      }
    }
  }
}
